import { FC, FormEventHandler, useEffect, useState } from "react";
import styles from "./edit-contract-aircraft.module.scss";
import { Modal } from "../../../../../ui/modal/Modal";
import { Button } from "../../../../../ui/button/Button";
import { z } from "zod";
import { Snackbar } from "../../../../../ui/snackbar/Snackbar";
import {
  useEditContractAircraftMutation,
  useGetAllContractAircraftsQuery,
} from "../contractAircraftApi";
import { useGetAllTransportQuery } from "../../airTransport/airTransportApi";
import { HHMM2min, min2HHMM } from "../../../../../helpers/time";
import { IContractWorkRow } from "../../contractWorks/IContractWork";

type Props = {
  id: number;
  contract_id: number;
  close: () => void;
};

type State = {
  errors?: {
    limit_time?: string[];
    cost?: string[];
  };
  message?: string | null;
};

const FormSchema = z.object({
  limit_time: z
    .string()
    .min(1, { message: "Введите лимит летного времени" })
    .transform((val) => {
      return HHMM2min(val);
    }),
  cost: z.coerce.number().positive({
    message: "Стоимость работ должна быть выше 0",
  }),
});

const Form: FC<Props> = ({ id, contract_id, close }) => {
  const initialState: State = { message: null, errors: {} };
  const [state, setState] = useState<State>(initialState);
  const [aircraft, setAircraft] = useState<IContractWorkRow | null>(null);
  const [isUXOpen, setIsUXOpen] = useState(true);
  const { data } = useGetAllContractAircraftsQuery(contract_id);
  const [edit, { isError, isLoading, isSuccess }] =
    useEditContractAircraftMutation();
  const {
    data: transport,
    isFetching: isTransportFetching,
    isError: isTransportError,
  } = useGetAllTransportQuery();

  const submit: FormEventHandler = (e) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    setState(initialState);
    setIsUXOpen(true);

    const validatedFields = FormSchema.safeParse({
      limit_time: formData.get("limit_time"),
      cost: formData.get("cost"),
    });

    if (!validatedFields.success) {
      setState({
        errors: validatedFields.error.flatten().fieldErrors,
        message: "Не удалось сохранить форму. Заполните все поля.",
      });
      return;
    }

    const { limit_time, cost } = validatedFields.data;
    edit({
      id,
      limit_time,
      cost,
    });
  };

  useEffect(() => {
    if (!isSuccess) return;
    setTimeout(() => {
      close();
    }, 1500);
  }, [isSuccess]);

  useEffect(() => {
    if (!data) return;
    const aircraft = data.find((c) => c.id === id);
    if (!aircraft) return;
    const work: IContractWorkRow = {
      id,
      limit_time: min2HHMM(aircraft.limit_time),
      cost: aircraft.cost / 100,
    };
    setAircraft(work);
  }, [data]);

  return (
    <form className={styles.form} onSubmit={submit}>
      <div className={styles.title}>Редактирование воздушного судна</div>
      <div className={styles.row}>
        <label htmlFor="limit_time">Лимит летного времени, часы:минуты</label>
        <input
          className={styles.input}
          name="limit_time"
          placeholder="Лимит летного времени, часы:минуты"
          defaultValue={aircraft?.limit_time}
        />
        <div id="limit_time-error" aria-live="polite" aria-atomic="true">
          {state.errors?.limit_time &&
            state.errors.limit_time.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="cost">Стоимость 1 часа, руб.</label>
        <input
          className={styles.input}
          name="cost"
          placeholder="Стоимость 1 часа, руб."
          defaultValue={aircraft?.cost}
        />
        <div id="cost-error" aria-live="polite" aria-atomic="true">
          {state.errors?.cost &&
            state.errors.cost.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>

      <div className={styles.actions}>
        <Button type="reset" onClick={() => setState(initialState)}>
          Очистить
        </Button>
        <Button type="submit" isProcessing={isLoading} isDisabled={isLoading}>
          Сохранить
        </Button>
      </div>
      {state.message && isUXOpen && (
        <Snackbar
          isOpen={Boolean(state.message)}
          className="error"
          onClose={() => setIsUXOpen(false)}
        >
          {state.message}
        </Snackbar>
      )}
      {isSuccess && (
        <Snackbar isOpen={isSuccess} className="success" onClose={close}>
          Изменения сохранены
        </Snackbar>
      )}
      {isError && isUXOpen && (
        <Snackbar
          isOpen={isError}
          className="error"
          onClose={() => setIsUXOpen(false)}
        >
          Ошибка сохранения. Попробуйте позже
        </Snackbar>
      )}
      {isTransportError && isUXOpen && (
        <Snackbar
          isOpen={isError}
          className="error"
          onClose={() => setIsUXOpen(false)}
          autoHideDuration={3000}
        >
          Не удалось загрузить список типов ВС.
        </Snackbar>
      )}
    </form>
  );
};

export const EditContractAircraft: FC<Props> = ({ close, contract_id, id }) => {
  return (
    <Modal close={close}>
      <Form close={close} contract_id={contract_id} id={id} />
    </Modal>
  );
};
